/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import { formatISODateOnly } from '../../utils/utils';

/**
 * Every event a day's Sync stored, per platform — the View popup on /installation-data-statics.
 *
 * The table keeps only pauser_app_install (its Android / iOS (BigQuery) columns) so it fits without
 * scrolling sideways; the other four events live here, one row each, read from the row already
 * loaded (no extra request). All are installs, each counted once, as BigQuery reported them.
 *
 * Closes on ×, Escape or a click on the backdrop; focus moves to × when it opens.
 *
 * Props: { row, onClose }  — row: a synced per-day row (android_bigquery_count, <event>_android…)
 */
export const EVENT_ROWS = [
  { event: 'pauser_app_install', label: 'Installs', android: 'android_bigquery_count', ios: 'ios_bigquery_count' },
  ...[
    ['first_open', 'First Open (Auto)'],
    ['custom_login_start', 'Sign In Started'],
    ['custom_sign_up_success', 'Signed Up'],
    ['custom_login_success', 'Signed In'],
  ].map(([e, label]) => ({ event: e, label, android: `${e}_android`, ios: `${e}_ios` })),
];

const n = (v) => (Number(v) || 0).toLocaleString();

// An event name may wrap only after an underscore on a narrow screen — never mid-word.
const breakable = (name) => name.split('_').flatMap((part, i, all) => (
  i < all.length - 1 ? [part, '_', <wbr key={i} />] : [part]
));

const InstallationEventsModal = ({ row, onClose }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!row) return null;
  const day = formatISODateOnly(row.time_stamp);
  const titleId = 'inst-events-title';

  return (
    <div className="au-modal-bg" data-testid="inst-events-backdrop" onClick={onClose}>
      <div
        className="au-modal inst-events-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" ref={closeRef} className="au-modal-close" onClick={onClose} aria-label="Close" title="Close">×</button>
        <h2 className="au-modal-title" id={titleId}>Events · {day}</h2>
        <p className="inst-events-caption">
          Installs that sent each event that day, per BigQuery — each install counted once.
        </p>
        <table className="au-modal-table inst-events-table">
          <thead>
            <tr><th>Event</th><th>Android</th><th>iOS</th><th>Total</th></tr>
          </thead>
          <tbody>
            {EVENT_ROWS.map(({ event, label, android, ios }) => {
              const a = Number(row[android]) || 0;
              const i = Number(row[ios]) || 0;
              return (
                <tr key={event} className={event === 'pauser_app_install' ? 'inst-events-main' : undefined}>
                  <td className="inst-events-name">
                    <span className="inst-events-label">{label}</span>{' '}
                    <span className="inst-events-code">({breakable(event)})</span>
                  </td>
                  <td className="inst-num">{n(a)}</td>
                  <td className="inst-num">{n(i)}</td>
                  <td className="inst-num inst-events-total">{n(a + i)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InstallationEventsModal;
